<template>
  <template v-if="hasText">
    <div
      ref="reference"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      @mousemove="handleMouseMove"
      @focusin="handleFocusIn"
      @focusout="handleFocusOut"
      :tabindex="focusable ? 0 : -1"
      :aria-label="ariaLabel"
      :aria-describedby="tooltipId"
      class="inline-block focus:outline-hidden"
      :class="[trigger === 'text' && 'hover-info', props.class]"
    >
      <slot />
      <span v-if="srText" class="sr-only"> {{ text }} </span>
    </div>

    <!-- Mounted only while open, so an idle tooltip parks nothing in the body (a
         table mounts one per row). It fades in and goes at once on close: held
         for a fade-out, reka's presence waits on an animationend.
         Positioned by shadcn's TooltipContent against this wrapper, or against
         the pointer when following the cursor. The open state stays here, so
         the delay, the grace period and the focus rules are this component's. -->
    <TooltipProvider v-if="open" :delay-duration="0">
      <UiTooltip :open="open" disable-hoverable-content @update:open="onRekaOpenChange">
        <TooltipContent
          :id="tooltipId"
          :reference="anchor"
          :side="followCursor && !openedByFocus ? 'bottom' : side"
          :align="followCursor && !openedByFocus ? 'start' : 'center'"
          :side-offset="followCursor && !openedByFocus ? 12 : 8"
          :aria-label="text"
          :class="[
            'whitespace-pre-line',
            align === 'center' ? 'text-center' : 'text-left',
            followCursor ? 'pointer-events-none' : 'pointer-events-auto',
            panelClass,
          ]"
          @mouseenter="handleFloatingEnter"
          @mouseleave="handleFloatingLeave"
          @focusin="handleFloatingEnter"
          @focusout="handleFloatingLeave"
        >
          <!-- Rich content takes over the visual tooltip; `text` stays the
               accessible/sr-only representation either way. -->
          <slot name="content">{{ text }}</slot>
        </TooltipContent>
      </UiTooltip>
    </TooltipProvider>
  </template>

  <!-- No tooltip: no tabindex and no hover handlers. A caller's class still
       applies, on the same inline-block box the tooltip branch draws, so the
       content looks the same whether or not there is anything to show. -->
  <div v-else-if="props.class" class="inline-block" :class="props.class"><slot /></div>
  <template v-else>
    <slot />
  </template>
</template>

<script setup>
import { ref, watchEffect, computed } from "vue";
import { Tooltip as UiTooltip, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { provideTooltipActive, useInsideActiveTooltip } from "@/components/kit/tooltipContext";

const props = defineProps({
  ariaLabel: { type: String, required: false },
  text: { type: String, default: "" },
  class: { type: [String, Array, Object], default: "" },
  followCursor: { type: Boolean, default: false },
  delay: { type: Number, default: 200 },
  // Text alignment inside the panel.
  align: {
    type: String,
    default: "left",
    validator: (value) => ["left", "center"].includes(value),
  },
  side: {
    type: String,
    default: "top",
    validator: (value) => ["top", "bottom", "left", "right"].includes(value),
  },
  // Padding and text size for the floating panel. Overriding replaces them, so a
  // caller can match another surface (HoverTime matches the IP popover's card).
  panelClass: { type: [String, Array, Object], default: "max-w-xs px-2 py-1.5 text-xs" },
  focusable: {
    type: Boolean,
    default: false,
  },
  childFocusShowsTooltip: {
    type: Boolean,
    default: false,
  },
  // Text that explains on hover wears the dotted underline (`hover-info`), so it
  // reads as explaining rather than going somewhere. A control (an icon, a
  // button, a pill, a disabled field) says so here and keeps its own look.
  // The line sits 4px below a one-line box, so a caller cuts its text sideways
  // only (`overflow-x-clip`, never `truncate`).
  trigger: {
    type: String,
    default: "text",
    validator: (value) => ["text", "control"].includes(value),
  },
  // The panel's text, read inline after the trigger. A control whose own name
  // already says it (CopyText's "Copy") turns this off.
  srText: {
    type: Boolean,
    default: true,
  },
});

const open = ref(false);
const reference = ref(null);
const mousePosition = ref({ x: 0, y: 0 });
const hoverTimeout = ref(null);
const tooltipId = `tooltip-${Math.random().toString(36).slice(2, 8)}`;
const openedByFocus = ref(false); // track if focus opened the tooltip

const hasText = computed(() => props.text?.trim()?.length > 0);

// Publish "a tooltip is live here", ORed with what we inherited, so a nested one can suppress itself.
const outerTooltipActive = useInsideActiveTooltip();
provideTooltipActive(computed(() => hasText.value || outerTooltipActive.value));

// What the panel is placed against: the wrapper, or a point just off the
// pointer while following the cursor (a virtual element reka's popper reads).
const anchor = computed(() => {
  if (!props.followCursor || openedByFocus.value) return reference.value;
  const { x, y } = mousePosition.value;
  return { getBoundingClientRect: () => new DOMRect(x, y, 0, 0) };
});

// reka reports Escape and an outside pointerdown as a request to close. The
// open requests it would make come from its trigger, which this does not use.
function onRekaOpenChange(value) {
  if (!value) {
    open.value = false;
    openedByFocus.value = false;
  }
}

// Grace period after leaving the trigger, long enough for the pointer to
// travel into the tooltip panel (which keeps it open while hovered).
const CLOSE_GRACE_MS = 150;
const closeTimeout = ref(null);

const cancelClose = () => {
  if (closeTimeout.value) {
    clearTimeout(closeTimeout.value);
    closeTimeout.value = null;
  }
};

const handleMouseEnter = () => {
  if (!hasText.value) return;
  cancelClose();
  openedByFocus.value = false;
  if (props.delay > 0) {
    hoverTimeout.value = setTimeout(() => {
      open.value = true;
    }, props.delay);
  } else {
    open.value = true;
  }
};

const handleMouseLeave = () => {
  if (hoverTimeout.value) {
    clearTimeout(hoverTimeout.value);
    hoverTimeout.value = null;
  }
  cancelClose();
  closeTimeout.value = setTimeout(() => {
    open.value = false;
  }, CLOSE_GRACE_MS);
};

// Hovering the panel itself keeps it open (except in follow-cursor mode,
// where the panel tracks the pointer and never receives events).
const handleFloatingEnter = () => {
  cancelClose();
};

const handleFloatingLeave = () => {
  cancelClose();
  open.value = false;
};

const handleMouseMove = (event) => {
  if (props.followCursor) {
    openedByFocus.value = false;
    mousePosition.value = { x: event.clientX, y: event.clientY };
  }
};

const handleFocusIn = () => {
  if (!hasText.value) return;
  if (!props.focusable && !props.childFocusShowsTooltip) return;
  open.value = true;
  openedByFocus.value = true;
};

const handleFocusOut = () => {
  open.value = false;
  openedByFocus.value = false;
};

const handleKeydown = (e) => {
  if (e.key === "Escape") {
    open.value = false;
    openedByFocus.value = false;
  }
};

watchEffect((onCleanup) => {
  if (open.value) {
    window.addEventListener("keydown", handleKeydown);
    onCleanup(() => window.removeEventListener("keydown", handleKeydown));
  }
});
</script>
